import DOMPurify from 'dompurify'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ALLOWED_CLASS_NAMES, SAFE_HOVERCARD_URL_PATTERNS} from './sanitize-constants'

// DOMPurify configuration for math-renderer elements
// SECURITY: ALLOW_DATA_ATTR must be false to block arbitrary data-* attributes
// We explicitly allow safe data-* attributes, but validate URLs in the hook below
const MATH_DOM_PURIFY_CONFIG = {
  ADD_TAGS: ['math-renderer'],
  ADD_ATTR: [
    'class',
    // Math renderer
    'data-run-id',
    'data-catalyst',
    // Hovercards - URLs validated in hook to prevent page-freeze attacks
    'data-hovercard-url',
    'data-hovercard-type',
    // Analytics (safe - no code execution)
    'data-octo-click',
    'data-octo-dimensions',
  ],
  ALLOW_DATA_ATTR: false,
}

/**
 * Validates hovercard URLs against a strict allowlist.
 * Uses fully-anchored regex patterns that only match exact valid hovercard paths.
 */
function isValidHovercardUrl(url: string): boolean {
  // Must be a relative path starting with /
  if (!url.startsWith('/')) {
    return false
  }

  // Decode URL for pattern matching (handles %2F etc in path segments)
  let decodedUrl: string
  try {
    decodedUrl = decodeURIComponent(url)
  } catch {
    // Invalid URL encoding - reject
    return false
  }

  // Strict allowlist: patterns are fully anchored (^ and $) so only exact matches pass
  // Path traversal like /memexes/0/hovercard/../../../evil won't match because of the $ anchor
  return SAFE_HOVERCARD_URL_PATTERNS.some(pattern => pattern.test(decodedUrl))
}

/**
 * Hook to sanitize class attributes and validate hovercard URLs.
 * - Class names: only allow explicitly listed classes, split on any whitespace
 * - Hovercard URLs: only allow known safe paths to prevent page-freeze attacks
 */
const sanitizeAttributeHook = (node: Element, data: {attrName: string; attrValue: string; keepAttr: boolean}) => {
  // Sanitize class attributes using allow list
  if (data.attrName === 'class' && data.attrValue) {
    // Split on any whitespace (including tabs, newlines, etc.) to catch bypass attempts
    const classes = data.attrValue.split(/\s+/)
    // Only keep non-empty class names that are explicitly allowed
    const safeClasses = classes.filter(cls => cls && ALLOWED_CLASS_NAMES.has(cls))
    if (safeClasses.length === 0) {
      data.keepAttr = false
    } else {
      data.attrValue = safeClasses.join(' ')
    }
  }

  // Validate hovercard URLs to prevent page-freeze attacks
  // Only allow known safe hovercard paths (users, orgs, issues, PRs, commits)
  if (data.attrName === 'data-hovercard-url' && data.attrValue) {
    if (!isValidHovercardUrl(data.attrValue)) {
      data.keepAttr = false
    }
  }
}

// Lazy-initialized DOMPurify instance (created on first use)
// This avoids errors in SSR environments where window is not available at module load time
let purifier: ReturnType<typeof DOMPurify> | null = null

function getPurifier(): ReturnType<typeof DOMPurify> {
  if (!purifier) {
    purifier = DOMPurify()
    purifier.addHook('uponSanitizeAttribute', sanitizeAttributeHook)
  }
  return purifier
}

/**
 * Sanitizes HTML content using an allow list for class names.
 * Only explicitly allowed class names are preserved; all others are stripped.
 * This prevents XSS attacks where attackers inject behavior-triggering classes
 * using whitespace bypass techniques (e.g., tabs between class names).
 *
 * Security fix for HackerOne https://github.com/github/issues/issues/19803
 */
export function sanitizeTaskListContent(html: string): SafeHTMLString {
  if (typeof window === 'undefined') {
    /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string --
       In SSR environments, return html as-is (sanitization happens client-side) */
    return html as SafeHTMLString
  }
  return getPurifier().sanitize(html, {
    ...MATH_DOM_PURIFY_CONFIG,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  }) as SafeHTMLString
}
